import ProductRow from './ProductRow';
import { PRODUCT_TABLE_COLUMNS } from '../../constants';
import { getMembershipTermString, shouldShowMembershipTerm } from '../../util/utils';
import { cn } from '@rocco/index';
import type { Proposal } from '../../type/types';

interface ProductTableSectionProps {
    proposal: Proposal;
    isMobile: boolean;
    /** Hide the tax column when all line items share the same tax rate */
    shouldHideTaxColumn: boolean;
    /** Hide the discount column when total discount is 0 */
    shouldHideDiscountColumn: boolean;
}

const TYPO_CLASS = {
    PRODUCTS_HEADER:
        'font-display font-semibold text-base leading-none tracking-[-0.01em] text-[#929292]',
    MEMBERSHIP_TERM: 'font-medium text-[0.75rem] md:text-[0.75rem]',
    MEMBERSHIP_TERM_VALUE: 'font-normal text-[0.75rem] md:text-[0.75rem]',
    HEADER_CLASS:
        'text-[0.54rem] font-medium text-[#9B9B9B] print:text-[0.6rem]',
};

/** Renders the product table with column headers and product rows. */
export const ProductTableSection = ({
    proposal,
    isMobile,
    shouldHideTaxColumn,
    shouldHideDiscountColumn,
}: ProductTableSectionProps) => {
    const hasTax = !shouldHideTaxColumn;
    const hasDiscount = !shouldHideDiscountColumn;
    const showMembershipTerm = shouldShowMembershipTerm(proposal);

    const visibleColumns = PRODUCT_TABLE_COLUMNS.filter((col) => {
        if (col.key === 'tax') return hasTax;
        if (col.key === 'discount') return hasDiscount;
        return true;
    });

    return (
        <div className="space-y-[0.5rem] pt-[2.1rem] md:space-y-[0.25rem] md:py-6 md:pt-[4.38rem] print:break-inside-avoid print:space-y-[0.25rem] print:pt-[4.38rem]">
            {/* Header */}
            <div className="mb-[0.25rem] flex md:mb-[0.82rem]">
                {visibleColumns.map((col) => {
                    if (isMobile) {
                        if (col.key === 'title') {
                            return (
                                <div key={'title'}>
                                    <span
                                        className={cn(
                                            TYPO_CLASS.PRODUCTS_HEADER,
                                            'pl-[0.2rem] md:pl-[0rem] print:pl-[0rem]',
                                        )}
                                    >
                                        Products
                                    </span>
                                </div>
                            );
                        }
                        return null;
                    }
                    if (col.key === 'title') {
                        return (
                            <div
                                key="title"
                                className={cn(
                                    col.className,
                                    'md:pl-[0.5rem] print:pl-[0.5rem]',
                                )}
                            >
                                {showMembershipTerm ? (
                                    <>
                                        <span
                                            className={TYPO_CLASS.MEMBERSHIP_TERM}
                                        >
                                            Membership term:
                                        </span>
                                        <span
                                            className={cn(
                                                TYPO_CLASS.MEMBERSHIP_TERM_VALUE,
                                                'md:ml-[1.38rem] print:ml-[1.38rem]',
                                            )}
                                        >
                                            {getMembershipTermString(proposal)}
                                        </span>
                                    </>
                                ) : null}
                                {/* Workaround, trigger downlaod font Allura manually */}
                                <span
                                    style={{
                                        fontFamily: 'Allura',
                                        color: '#FFFFFF',
                                    }}
                                >
                                    .
                                </span>
                            </div>
                        );
                    }

                    return (
                        <div
                            key={col.key}
                            className={cn(
                                TYPO_CLASS.HEADER_CLASS,
                                col.className,
                                'md:pt-[0.3rem]',
                            )}
                        >
                            {col.header}
                        </div>
                    );
                })}
            </div>

            {proposal.productItems?.map((productItem) => (
                <ProductRow
                    productItem={productItem}
                    currency={proposal.currency}
                    key={productItem.id}
                    isMobile={isMobile}
                    showTaxColumn={hasTax}
                    showDiscountColumn={hasDiscount}
                />
            )) || []}
        </div>
    );
};
